/**
 *
 *  Copyright (c) "Neo4j"
 *  Neo4j Sweden AB [http://neo4j.com]
 *
 *  This file is part of Neo4j.
 *
 *  Neo4j is free software: you can redistribute it and/or modify
 *  it under the terms of the GNU General Public License as published by
 *  the Free Software Foundation, either version 3 of the License, or
 *  (at your option) any later version.
 *
 *  This program is distributed in the hope that it will be useful,
 *  but WITHOUT ANY WARRANTY; without even the implied warranty of
 *  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 *  GNU General Public License for more details.
 *
 *  You should have received a copy of the GNU General Public License
 *  along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */
import {
  autoUpdate,
  type AutoUpdateOptions,
  flip,
  offset,
  type Placement,
  type ReferenceType,
  safePolygon,
  shift,
  useClick,
  useDismiss,
  useFloating,
  type UseFloatingOptions,
  type UseFloatingReturn,
  useFocus,
  useHover,
  useInteractions,
  type UseInteractionsReturn,
  useRole,
} from '@floating-ui/react';
import {
  createContext,
  useContext,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';

export interface TooltipOptions {
  isInitialOpen?: boolean;
  placement?: Placement;
  isOpen?: boolean;
  onOpenChange?: UseFloatingOptions['onOpenChange'];
  type?: 'simple' | 'rich';
  isPortaled?: boolean;
  strategy?: 'fixed' | 'absolute';
  hoverDelay?: {
    open: number;
    close: number;
  };
  shouldCloseOnReferenceClick?: boolean;
  autoUpdateOptions?: AutoUpdateOptions;
  isDisabled?: boolean;
  typeOpened?: 'keyboard' | 'mouse';
}

export function useTooltip({
  isInitialOpen = false,
  placement = 'top',
  isOpen: controlledOpen,
  onOpenChange: setControlledOpen,
  type = 'simple',
  isPortaled = true,
  strategy = 'absolute',
  hoverDelay = undefined,
  shouldCloseOnReferenceClick = false,
  autoUpdateOptions,
  isDisabled = false,
}: TooltipOptions = {}): {
  headerId?: string;
  setHeaderId?: (id: string | undefined) => void;
  isOpen: boolean;
  setOpen: (open: boolean) => void;
  type: 'simple' | 'rich';
  isPortaled: boolean;
  typeOpened: 'keyboard' | 'mouse' | null;
  setTypeOpened: (type: 'keyboard' | 'mouse' | null) => void;
} & UseInteractionsReturn &
  UseFloatingReturn<ReferenceType> {
  const id = useId();
  const [headerId, setHeaderId] = useState<string | undefined>(id);
  const [isUncontrolledOpen, setIsUncontrolledOpen] = useState(isInitialOpen);
  const isOpen = controlledOpen ?? isUncontrolledOpen;
  const setOpen = setControlledOpen ?? setIsUncontrolledOpen;
  const typeOpenedRef = useRef<'keyboard' | 'mouse'>(null);

  const data = useFloating({
    middleware: [
      offset(5),
      flip({
        crossAxis: placement.includes('-'),
        fallbackAxisSideDirection: 'start',
        padding: 5,
      }),
      shift({ padding: 5 }),
    ],
    onOpenChange: setOpen,
    open: isOpen,
    placement,
    strategy,
    whileElementsMounted(referenceEl, floatingEl, update) {
      const cleanup = autoUpdate(referenceEl, floatingEl, update, {
        ...autoUpdateOptions,
      });
      return cleanup;
    },
  });

  const context = data.context;

  const hover = useHover(context, {
    delay: hoverDelay,
    enabled: type === 'simple' && !isDisabled,
    handleClose: safePolygon(),
    move: false,
  });
  const click = useClick(context, {
    enabled: type === 'rich' && !isDisabled,
  });
  const focus = useFocus(context, {
    enabled: type === 'simple' && !isDisabled,
    visibleOnly: true,
  });
  const dismiss = useDismiss(context, {
    escapeKey: true,
    outsidePress: true,
    referencePress: shouldCloseOnReferenceClick,
  });
  const role = useRole(context, {
    role: type === 'simple' ? 'tooltip' : 'dialog',
  });

  const interactions = useInteractions([hover, focus, dismiss, role, click]);

  const setTypeOpened = (type: 'keyboard' | 'mouse' | null) => {
    typeOpenedRef.current = type;
  };

  return useMemo(
    () => ({
      headerId: type === 'rich' ? headerId : undefined,
      isOpen,
      isPortaled,
      setHeaderId: type === 'rich' ? setHeaderId : undefined,
      setOpen,
      setTypeOpened,
      type,
      typeOpened: typeOpenedRef.current,
      ...interactions,
      ...data,
    }),
    [
      headerId,
      isOpen,
      setOpen,
      type,
      typeOpenedRef,
      isPortaled,
      interactions,
      data,
    ],
  );
}

type ContextType = ReturnType<typeof useTooltip> | null;
export const TooltipContext = createContext<ContextType>(null);

export const useTooltipContext = (): ReturnType<typeof useTooltip> => {
  const context = useContext(TooltipContext);

  if (context === null) {
    throw new Error('Tooltip components must be wrapped in <Tooltip />');
  }

  return context;
};
